/* eslint-disable @typescript-eslint/no-shadow */

import React, { useEffect } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import {
  KIcon,
  KTextInput,
  KButton,
  KDialog,
} from "@kyburz/kdesign-system";
import useSetState from "../../hooks/useSetState";
import {
  createFullTemplate,
  deleteFullTemplate,
  editFullTemplate,
  getFullTemplates,
} from "../../apiCalls/config/fullTemplates";
import FullTemplateView from "./FullTemplateView";
import TableDropdown from "../../components/TableDropdown";
import { formatDate } from "../../utils/timeUtils";
import { setAlert } from "../../redux/actions/actions";
import KInfiniteTable from "../../components/table/KInfiniteTable";

const MemoizedKInfiniteTable = React.memo(
  (props: any) => (
    <KInfiniteTable
      data={props.data}
      hasNextPage={props.hasNextPage}
      isNextPageLoading={props.isNextPageLoading}
      loadNextPage={props.loadNextPage}
      columns={props.columns}
      updateMyData={props.updateMyData}
      hasFilter
    />
  ),
  (oldProps, newProps) => {
    return oldProps.data === newProps.data;
  },
);

const useStyles = makeStyles(() => ({
  ctaContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  tableContainer: {
    height: "calc(100% - 100px)",
  },
  iconContainer: {
    display: "inline-block",
    cursor: "pointer",
  },
  tableDropDownWrapper: {
    cursor: "pointer",
    display: "flex",
    justifyContent: "flex-end",
    marginRight: 14,
  },
}));

const FullTemplates: React.FC = () => {
  const classes = useStyles();
  const firstUpdate = React.useRef(true);
  const { CancelToken } = axios;
  const dispatch = useDispatch();

  const [
    {
      query,
      pagesLoaded,
      hasNextPage,
      isNextPageLoading,
      items,
      cancelTokenSource,
      fullTemplateViewOpen,
      editTemplateName,
      reload,
      deleteModalOpen,
      fullTemplateToDelete,
    },
    setState,
  ] = useSetState({
    query: "",
    pagesLoaded: 0,
    hasNextPage: true,
    isNextPageLoading: false,
    items: [],
    cancelTokenSource: null,
    fullTemplateViewOpen: false,
    editTemplateName: "",
    reload: false,
    deleteModalOpen: false,
    fullTemplateToDelete: "",
  });

  const handleChange = (e) => setState({ [e.target.name]: e.target.value });

  const toggleFullTemplateView = (reloadValue = false) => {
    if (fullTemplateViewOpen) {
      setState({ editTemplateName: "" });
    }
    setState({
      fullTemplateViewOpen: !fullTemplateViewOpen,
      reload: reloadValue,
    });
  };

  const loadTemplatesPage = (query, page, startEmpty = true) => {
    if (cancelTokenSource !== null) {
      cancelTokenSource.cancel("Search cancelled");
    }
    const source = CancelToken.source();

    setState({ isNextPageLoading: true, cancelTokenSource: source });
    getFullTemplates(query, page, source.token)
      .then((res) => {
        const previousArray = startEmpty ? [] : items;
        setState({
          items: [...previousArray].concat(
            ...res.data.map((x) => ({
              name: x.name,
              description: x.description || "",
              templateName: x.template_name || "",
              transformationName: x.transformation_name || "",
              tags: Object.keys(x.transformation_tags) || "",
              createdAt: x.created_at || "",
              updatedAt: x.updated_at || "",
            })),
          ),
          isNextPageLoading: false,
          pagesLoaded: page,
          hasNextPage: false,
          cancelToken: null,
        });
      })
      .catch((e) => {
        setState({
          isNextPageLoading: false,
          pagesLoaded: page,
          cancelToken: null,
        });
        console.error(e);
      });
  };

  const loadNextPage = () => {
    loadTemplatesPage(query, pagesLoaded + 1, false);
  };

  const handleSave = (
    fullTemplateName: string,
    description: string,
    templateName: string,
    transformationName: string,
    edit?: string,
  ) => {
    if (edit) {
      editFullTemplate(
        fullTemplateName,
        description,
        templateName,
        transformationName,
      ).then((res) => {
        if (res.data) {
          dispatch(setAlert(`Success updating ${edit}`, "success"));
          loadTemplatesPage(query, 1, true);
        } else {
          dispatch(setAlert(`Error updating ${edit}`, "error"));
        }
      });
    } else {
      createFullTemplate(
        fullTemplateName,
        description,
        templateName,
        transformationName,
      ).then((res) => {
        if (res.data) {
          dispatch(setAlert(`Success creating full template!`, "success"));
          loadTemplatesPage(query, 1, true);
        } else {
          dispatch(setAlert(`Error creating full template`, "error"));
        }
      });
    }
  };

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    loadTemplatesPage(query, 1, true);
  }, [query]);

  useEffect(() => {
    if (reload) {
      loadTemplatesPage(query, 1, true);
    }
  }, [reload]);

  const handleEditClick = (row: string) => {
    setState({ editTemplateName: row });
    toggleFullTemplateView();
  };

  const handleDelete = () => {
    deleteFullTemplate(fullTemplateToDelete)
      .then(() => {
        dispatch(
          setAlert(`Successfully deleted ${fullTemplateToDelete}`, "success"),
        );
        setState({
          deleteModalOpen: false,
          fullTemplateToDelete: "",
        });
        loadTemplatesPage(query, 1, true);
      })
      .catch((e) => {
        setState({
          deleteModalOpen: false,
        });
        dispatch(
          setAlert(
            `Error deleting full template ${fullTemplateToDelete}`,
            "error",
          ),
        );
        console.error(e);
      });
  };

  const toggleDeletePopup = () =>
    setState({ deleteModalOpen: !deleteModalOpen, fullTemplateToDelete: "" });

  const deletePopup = (
    <KDialog
      size="xs"
      title="Are you sure?"
      open={deleteModalOpen}
      primaryCTAonClick={handleDelete}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={() => toggleDeletePopup()}
      secondaryCTAText="Cancel"
    >
      <p>
        Are you sure you want to delete the full template{" "}
        <b>{fullTemplateToDelete}</b>?
      </p>
    </KDialog>
  );

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        defaultCanSort: true,
      },
      {
        Header: "Description",
        accessor: "description",
        defaultCanSort: true,
      },
      {
        Header: "Tags",
        accessor: "tags",
      },
      {
        Header: "Created",
        accessor: (row) => formatDate(row.createdAt),
        defaultCanSort: true,
      },
      {
        Header: "Updated",
        accessor: (row) => formatDate(row.updatedAt),
        defaultCanSort: true,
      },
      {
        Header: "Template Name",
        accessor: "templateName",
        defaultCanSort: true,
      },
      {
        Header: "Transformation Name",
        accessor: "transformationName",
        defaultCanSort: true,
      },
      {
        accessor: "actions",
        sticky: "right",
        width: 80,
        disableFilters: true,
        Cell: (row) => {
          return (
            <div className={classes.tableDropDownWrapper}>
              <TableDropdown
                menuItems={[
                  {
                    icon: "edit",
                    title: "Edit",
                    action: () => handleEditClick(row?.row?.original?.name),
                  },
                  {
                    icon: "trash-2",
                    title: "Delete",
                    disabled: false,
                    action: () =>
                      setState({
                        deleteModalOpen: true,
                        fullTemplateToDelete: row.row.original.name,
                      }),
                  },
                ]}
              />
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <>
      <div className={classes.ctaContainer}>
        <KTextInput
          placeholder="Search full templates..."
          value={query}
          onChange={handleChange}
          name="query"
        />
        <KButton
          children="New Full Template"
          icon={<KIcon icon="plus" />}
          onClick={() => toggleFullTemplateView()}
        />
      </div>
      <div className={classes.tableContainer}>
        <MemoizedKInfiniteTable
          loadNextPage={loadNextPage}
          isNextPageLoading={isNextPageLoading}
          columns={columns}
          data={items}
          hasNextPage={hasNextPage}
        />
      </div>
      <FullTemplateView
        open={fullTemplateViewOpen}
        edit={editTemplateName}
        handleClose={toggleFullTemplateView}
        onSave={handleSave}
      />
      {deletePopup}
    </>
  );
};

export default FullTemplates;
